import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Combobox } from '@/components/ui/combobox';
import { BillingNav } from './BillingNav';
import {
  BillingDocument,
  BillingDocType,
  BillingStatus,
  ComboboxClient,
  STATUS_LABEL,
  TYPE_LABEL,
  billingApi,
  clientDisplayName,
  euro,
} from '@/services/billingApi';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';

export default function BillingListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<BillingDocument[]>([]);
  const [clients, setClients] = useState<ComboboxClient[]>([]);
  const [loading, setLoading] = useState(true);
  const type = (params.get('type') || '') as BillingDocType | '';
  const status = (params.get('status') || '') as BillingStatus | '';
  const clientId = params.get('clientId') || '';
  const search = params.get('search') || '';

  useEffect(() => {
    apiClient
      .get('/clients/combobox')
      .then((r) => {
        const list = (r.data.data?.clients || r.data.data || []) as ComboboxClient[];
        setClients(Array.isArray(list) ? list : []);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    setLoading(true);
    const q: Record<string, string> = {};
    if (type) q.type = type;
    if (status) q.status = status;
    if (clientId) q.clientId = clientId;
    if (search) q.search = search;
    billingApi
      .list(q)
      .then(setRows)
      .catch((e) => toast.error(e.response?.data?.message || 'Errore caricamento'))
      .finally(() => setLoading(false));
  }, [type, status, clientId, search]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const clientOptions = useMemo(
    () => [
      { value: '', label: 'Tutti i clienti' },
      ...clients.map((c) => ({
        value: c.id,
        label: clientDisplayName(c),
        description: c.email || undefined,
      })),
    ],
    [clients]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Preventivi e conti</h1>
          <p className="text-sm text-gray-500">Documenti, pagamenti ed estratto conto dello studio</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/preventivi-e-conti/nuovo?type=INVOICE')}>
            Nuova fattura
          </Button>
          <Button onClick={() => navigate('/preventivi-e-conti/nuovo?type=QUOTE')}>
            <Plus className="h-4 w-4 mr-1" />
            Nuovo preventivo
          </Button>
        </div>
      </div>
      <BillingNav />
      <div className="flex flex-wrap gap-2 items-center">
        {(['', 'QUOTE', 'INVOICE', 'CREDIT_NOTE'] as const).map((t) => (
          <Button
            key={t || 'all'}
            size="sm"
            variant={type === t ? 'default' : 'outline'}
            onClick={() => setParam('type', t)}
          >
            {t ? TYPE_LABEL[t] : 'Tutti'}
          </Button>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-2">
        <Combobox
          options={clientOptions}
          value={clientId}
          onValueChange={(v) => setParam('clientId', v)}
          placeholder="Filtra cliente"
          searchPlaceholder="Cerca cliente..."
        />
        <select
          className="border rounded-md h-10 px-2"
          value={status}
          onChange={(e) => setParam('status', e.target.value)}
        >
          <option value="">Tutti gli stati</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <Input
          placeholder="Cerca numero o cliente"
          defaultValue={search}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setParam('search', (e.target as HTMLInputElement).value.trim());
            }
          }}
        />
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="p-3">Tipo</th>
              <th className="p-3">Numero</th>
              <th className="p-3">Cliente</th>
              <th className="p-3">Stato</th>
              <th className="p-3 text-right">Totale</th>
              <th className="p-3">Data</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="p-6 text-gray-500" colSpan={6}>
                  Caricamento...
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500" colSpan={6}>
                  Nessun documento
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className="border-b hover:bg-gray-50">
                <td className="p-3">{TYPE_LABEL[row.type]}</td>
                <td className="p-3">
                  <Link className="text-blue-600 font-medium" to={`/preventivi-e-conti/${row.id}`}>
                    {row.numberDisplay || 'Bozza'}
                    {row.revision > 1 ? ` rev.${row.revision}` : ''}
                  </Link>
                </td>
                <td className="p-3">
                  {row.clientId ? (
                    <Link className="text-blue-600" to={`/clients/${row.clientId}`}>
                      {row.client?.displayName}
                    </Link>
                  ) : (
                    row.client?.displayName
                  )}
                </td>
                <td className="p-3">
                  <Badge variant="secondary">{STATUS_LABEL[row.status]}</Badge>
                </td>
                <td className="p-3 text-right font-medium">{euro(row.total)}</td>
                <td className="p-3 text-gray-500">
                  {new Date(row.issuedAt || row.createdAt).toLocaleDateString('it-IT')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
